import {FC, useEffect, useState} from 'react'
import {ChildrenInterface} from '../../../global'
import {initialState, UserContext, UserInterface} from './types'
import axios from 'axios'
import {API_URL} from '../all-pages'
import {Redirect, useLocation} from 'react-router-dom'
import {useQuery} from 'react-query'
import {FallbackView} from '../../../_metronic/partials'

const GET_USER = process.env.REACT_APP_GET_USER || 'get_user'
const INVALID_SESSION_ROUTE = '/error/session-invalid'
const AUTH_STORAGE_KEYS = ['_token', '_userInterface', '_clientCreationDate'] as const

const clearAuthStorage = (): void => {
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
}

const extractTokenFromPath = (pathname: string): string | null => {
    const tokenMarker = '/token/'
    const markerIndex = pathname.indexOf(tokenMarker)
    if (markerIndex === -1) return null

    const rawToken = pathname.slice(markerIndex + tokenMarker.length)
    const normalizedToken = decodeURIComponent(rawToken.split('?')[0].split('#')[0]).trim()

    return normalizedToken || null
}

const isTokenFormatValid = (token: string): boolean => {
    return /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token)
}

const fetchUserData = async (token: string): Promise<UserInterface> => {
    const response = await axios({
        method: 'GET',
        url: `${API_URL}${GET_USER}`,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })

    return response.data
}

export const UserProvider: FC<ChildrenInterface> = ({children}) => {
    const [personalInformation, setPersonalInformation] = useState<UserInterface>(initialState)
    const {pathname} = useLocation()
    const storedToken = localStorage.getItem('_token')
    const urlToken = extractTokenFromPath(pathname)
    const activeToken = urlToken || storedToken
    const hasValidToken = !!activeToken && isTokenFormatValid(activeToken)

    useEffect(() => {
        if (activeToken && !hasValidToken) {
            clearAuthStorage()
        }
    }, [activeToken, hasValidToken])

    const {isLoading, error} = useQuery<UserInterface, Error>(
        ['userData', activeToken],
        () => fetchUserData(activeToken as string),
        {
            enabled: hasValidToken,
            onSuccess: (data) => {
                localStorage.setItem('_userInterface', JSON.stringify({data, timestamp: Date.now()}))
                localStorage.setItem('_clientCreationDate', data.created_at)
                localStorage.setItem('_token', activeToken as string)
                setPersonalInformation(data)

                if (urlToken) {
                    window.location.replace('/dashboard')
                }
            },
            onError: (err) => {
                console.error('Error fetching user data:', err)
                if (axios.isAxiosError(err) && err.response?.status === 401) {
                    clearAuthStorage()
                    // window.location.replace(INVALID_SESSION_ROUTE)
                }
            },
        }
    )

    if (activeToken && !hasValidToken) return <Redirect to={INVALID_SESSION_ROUTE} />
    if (isLoading) return <FallbackView />
    if (error) return <div>Error: {error.message}</div>

    return (
        <UserContext.Provider value={{personalInformation, setPersonalInformation}}>
            {children}
        </UserContext.Provider>
    )
}
